// authSlice.js
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { LoginAction, Role } from '../models/AccountEnums';
import { appViewApi } from './appViewApi';
import authApi from './authApi';
import { normalizeRole, normalizeRoles } from '@/utils/teamMemberRole';

/**
 * Detect cross-experience impersonation params in the URL.
 * When present, we defer setting `currentRole` until the impersonation
 * mutation (e.g. AccessInvestor) completes.  This prevents the caller's
 * dashboard (Advisor) from flashing before the target dashboard (Investor).
 */
const hasImpersonationAction = (): boolean => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('impersonationAction');
};

export type PendingRoleTerms = {
  roleName: Role;
  termId: number;
  termsHtml: string;
};

type AuthState = {
  isAuthenticated: boolean;
  faceIdEnabled?: boolean;
  user: string | null;
  token: string | null;
  error: string | null;
  roles?: Array<Role>;
  currentRole?: Role;
  primaryRole?: Role;
  pendingRoleTerms?: PendingRoleTerms;
  pendingRoleTwoFactor?: Role;
  pendingImpersonation: boolean;
  /** True from the moment a role switch starts until the new role's view has loaded. */
  isRoleSwitching?: boolean;
  isAppViewRedirecting?: boolean;
};

const initialState: AuthState = {
  isAuthenticated: false,
  faceIdEnabled: false,
  user: null,
  token: null,
  error: null,
  roles: [],
  pendingImpersonation: hasImpersonationAction(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (
      state,
      action: PayloadAction<{ user: string; token: string }>,
    ) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.error = null;
    },
    logoutSuccess: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.roles = [];
      state.currentRole = undefined;
      state.primaryRole = undefined;
      state.pendingRoleTerms = undefined;
      state.isRoleSwitching = false;
      state.isAppViewRedirecting = false;
      state.pendingRoleTwoFactor = undefined;
    },
    setFaceIdState: (state, action: PayloadAction<boolean>) => {
      state.faceIdEnabled = action.payload;
    },
    setRoleSwitching: (state, action: PayloadAction<boolean>) => {
      state.isRoleSwitching = action.payload;
    },
    setAppViewRedirecting: (state, action: PayloadAction<boolean>) => {
      state.isAppViewRedirecting = action.payload;
    },
    setCurrentRole: (state, action: PayloadAction<Role>) => {
      state.currentRole = action.payload;
    },
    setPendingRoleTerms: (
      state,
      action: PayloadAction<PendingRoleTerms | undefined>,
    ) => {
      state.pendingRoleTerms = action.payload;
    },
    setPendingRoleTwoFactor: (
      state,
      action: PayloadAction<Role | undefined>,
    ) => {
      state.pendingRoleTwoFactor = action.payload;
    },
    authError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      authApi.endpoints.loginUser.matchFulfilled,
      (state, action) => {
        if (
          'action' in action.payload &&
          action.payload.action === LoginAction.Home
        ) {
          state.isAuthenticated = true;
        }
        if (action.payload.success) {
          state.user = action.meta.arg.originalArgs.UserName;
          try {
            sessionStorage.removeItem('wa:authenticatedViaLegacy');
          } catch {
            /* noop */
          }
        }
        if ('roles' in action.payload && action.payload.roles) {
          state.roles = normalizeRoles(action.payload.roles);
          state.currentRole = normalizeRole(action.payload.currentRole);
          state.primaryRole = normalizeRole(action.payload.primaryRole);
        }
      },
    );
    builder.addMatcher(
      authApi.endpoints.submitSecurityAnswer.matchFulfilled,
      (state, action) => {
        if (action.payload.success) {
          state.isAuthenticated = true;
          state.user = action.meta.arg.originalArgs.username;
          state.roles = normalizeRoles(action.payload.roles);
          state.currentRole = normalizeRole(action.payload.currentRole);
          state.primaryRole = normalizeRole(action.payload.primaryRole);

          appViewApi.endpoints.getAppViewByRole.initiate(state.currentRole);
        }
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessInvestor.matchFulfilled,
      (state) => {
        state.isAuthenticated = true;
        state.currentRole = Role.Investor;
        state.pendingImpersonation = false;
        if (state.roles) {
          if (!state.roles.includes(Role.Investor)) {
            state.roles.push(Role.Investor);
          }
        } else {
          state.roles = [Role.Investor];
        }
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessAdvisor.matchFulfilled,
      (state) => {
        state.isAuthenticated = true;
        state.currentRole = Role.Advisor;
        state.pendingImpersonation = false;
        if (state.roles) {
          if (!state.roles.includes(Role.Advisor)) {
            state.roles.push(Role.Advisor);
          }
        } else {
          state.roles = [Role.Advisor];
        }
      },
    );
    builder.addMatcher(
      authApi.endpoints.accessAdvisorForTeamMember.matchFulfilled,
      (state) => {
        state.isAuthenticated = true;
        state.currentRole = Role.Advisor;
        state.pendingImpersonation = false;
        if (state.roles) {
          if (!state.roles.includes(Role.Advisor)) {
            state.roles.push(Role.Advisor);
          }
        } else {
          state.roles = [Role.Advisor];
        }
      },
    );
    builder.addMatcher(authApi.endpoints.logout.matchPending, (state) => {
      authSlice.caseReducers.logoutSuccess(state);
    });

    builder.addMatcher(
      authApi.endpoints.checkAuthStatus.matchFulfilled,
      (state, action) => {
        if (
          action.payload.success &&
          'username' in action.payload &&
          action.payload.username
        ) {
          state.isAuthenticated = true;
          state.user = action.payload.username;
          try {
            sessionStorage.setItem('wa:authenticatedViaLegacy', 'true');
          } catch {
            /* noop */
          }
        } else {
          state.isAuthenticated = false;
          state.user = null;
          authSlice.caseReducers.logoutSuccess(state);
        }

        if ('roles' in action.payload && action.payload.roles) {
          state.roles = normalizeRoles(action.payload.roles);
          state.primaryRole = normalizeRole(action.payload.primaryRole);
          // When an impersonation action is pending, don't set currentRole yet.
          // This prevents useGetAppViewByRoleQuery from firing the caller's
          // dashboard (e.g. Advisor) before AccessInvestor completes.
          if (!state.pendingImpersonation) {
            state.currentRole = normalizeRole(action.payload.currentRole);
          }
        }
      },
    );

    builder.addMatcher(
      authApi.endpoints.checkAuthStatus.matchRejected,
      (state) => {
        authSlice.caseReducers.logoutSuccess(state);
      },
    );
  },
});

export const {
  loginSuccess,
  logoutSuccess,
  authError,
  setFaceIdState,
  setCurrentRole,
  setPendingRoleTerms,
  setRoleSwitching,
  setAppViewRedirecting,
  setPendingRoleTwoFactor,
} = authSlice.actions;

export default authSlice.reducer;
